import { Navigate, Outlet, Route, Routes } from "react-router";

import { AppLayout } from "@/components/app-layout";
import { ErrorBoundary } from "@/components/error-boundary";
import { AdminRoute, GuestRoute, ProtectedRoute } from "@/components/protected-route";
import { AdminActivityPage } from "@/features/admin/activity";
import { AdminProjectsPage } from "@/features/admin/projects";
import { AdminUsersPage } from "@/features/admin/users";
import { ClientsPage } from "@/features/clients";
import { ForgotPasswordPage } from "@/features/forgot-password";
import { GitHubCallbackPage } from "@/features/github-callback";
import { HomePage } from "@/features/home";
import { HowToPage } from "@/features/how-to";
import { LoginPage } from "@/features/login";
import { McpAuthorizePage } from "@/features/mcp-authorize";
import { NotFoundPage } from "@/features/not-found";
import { ResetPasswordPage } from "@/features/reset-password";
import { SignupPage } from "@/features/signup";
import { VerifyEmailPage } from "@/features/verify-email";
import { YouPage } from "@/features/you";

export function App() {
  return (
    <Routes>
      {/* Top-level layout route purely to attach the error boundary to the
          whole tree (the SPA replacement for global-error.tsx). */}
      <Route ErrorBoundary={ErrorBoundary} element={<Outlet />}>
        <Route path="/" element={<Navigate to="/home" replace />} />

        {/* Public auth pages — bounce to /home if already signed in. */}
        <Route element={<GuestRoute />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* PUBLIC and never redirected, even mid-session — it reads the
            Supabase recovery token from the URL hash, which a redirect would
            destroy. Deliberately outside GuestRoute. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Authenticated app. */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/you" element={<YouPage />} />
            <Route path="/how-to" element={<HowToPage />} />
            <Route path="/how-to/:section" element={<HowToPage />} />
            <Route path="/github/callback" element={<GitHubCallbackPage />} />
            {/* OAuth consent for MCP clients. Protected on purpose: approving
                a connection has to be tied to a signed-in human. */}
            <Route path="/mcp/authorize" element={<McpAuthorizePage />} />

            {/* Admin-only, nested inside the authenticated shell. */}
            <Route element={<AdminRoute />}>
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/projects" element={<AdminProjectsPage />} />
              <Route path="/admin/activity" element={<AdminActivityPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
